import React, { useContext, useEffect, useState } from 'react'

import { Search } from '@mui/icons-material'
import { where } from 'firebase/firestore'
import { uniqBy } from 'lodash'

import ViewsLoader from '../../../loader/ViewsLoader'
import { IconPlus } from '../../../icons/IconPlus'
import MainModal from '../../../modals/common/MainModal'
import customImageComponent from '../../../common/CustomImageComponent'

import { getUserFullName } from '../../../../helpers/helpers'

import { UserModel } from '../../../../models/users/user.model'
import { IUserInterface } from '../../../../models/users/user.interface'

import FirestoreService from '../../../../services/firestoreService'
import { HandleModalContext } from '../../../../layout/mainlayout/MainLayout'

import { CONST } from '../../../../const/const'
import { MODAL_CONSTS } from '../../../../const/modal-const'

import verifiedIcon from './assets/verified-icon.svg'

const COLLECTIONS = CONST.DATA.FIRESTORE.V01.COLLECTIONS

export const STAFF_TYPES = [
  { id: 1, title: 'Show manager' },
  { id: 2, title: 'Secretary' },
  { id: 3, title: 'Technical Delegate' },
  { id: 4, title: 'Course Designers' },
  { id: 5, title: 'Judges' },
  { id: 6, title: 'Scorers' },
  { id: 7, title: 'Stewards' },
  { id: 8, title: 'Ingates' },
  { id: 9, title: 'Announcers' },
  { id: 10, title: 'Farriers' },
  { id: 11, title: 'Veterinarians' },
  { id: 12, title: 'Volunteers' },
  { id: 13, title: 'Other' },
]

const SelectBox: React.FC<{
  member: IUserInterfaceNew
  selected: boolean
  onClick: (caption: IUserInterfaceNew) => void
}> = ({ member, selected, onClick }) => (
  <div
    className="cursor-pointer mb-4 rounded-2xl flex items-center gap-4 text-[14px] border-[#D3DAEE]"
    onClick={() => onClick(member)}
  >
    <div className="w-[40px] h-[40px] min-w-[40px] rounded-full mr-2 overflow-hidden bg-gray-100">
      {customImageComponent(member.userProfilePicture, getUserFullName(member))}
    </div>
    <div className="flex-1">
      <div className="capitalize">{getUserFullName(member)}</div>
      <div className="opacity-50">{member.userEmail}</div>
    </div>
    <div>{selected && <img src={verifiedIcon} alt="verified" />}</div>
  </div>
)

interface IUserInterfaceNew extends IUserInterface {
  selected?: boolean
}

const StaffModal: React.FC<{
  onHide?: () => void
  onChange?: (location: any) => void
  dataToPassOn: {
    row?: IUserInterfaceNew[]
    id: number
    onSaveStaff: (value: { selected: IUserInterface[] }) => Promise<void>
    showAddNewPersonFeature?: boolean
  }
  title?: string
}> = ({ dataToPassOn }) => {
  const handleModalContext = useContext(HandleModalContext)

  const [loading, setLoading] = useState(false)
  const [buttonLoading, setButtonLoading] = useState(false)

  const { row } = dataToPassOn
  const [searchValue, setSearchValue] = useState<string>('')
  const [userTeamData, setUserTeamData] = useState<IUserInterfaceNew[]>([])

  useEffect(() => {
    const selectedRow = row?.map((item) => ({ ...item, selected: true })) ?? []
    setUserTeamData(selectedRow)
  }, [row])

  const title =
    dataToPassOn.id < 14 && dataToPassOn.id > 0
      ? STAFF_TYPES[dataToPassOn.id - 1].title
      : STAFF_TYPES.at(-1)?.title

  const handleUsers = async (strToSearch: string) => {
    const search = strToSearch.toLowerCase().replace(/\s+/g, '')
    setSearchValue(strToSearch)

    try {
      if (search.length < 3) {
        setUserTeamData(userTeamData.filter((user) => user.selected))
        return
      }

      setLoading(true)

      const usersInDb: IUserInterface[] = []
      const usersSnaps = await FirestoreService.filterItems(COLLECTIONS.USERS.NAME, [
        where(COLLECTIONS.USERS.FIELDS.USER_NAME_NGRAMS.KEY, 'array-contains', search),
      ])
      usersSnaps.forEach((currDoc) => {
        const horse = UserModel.fromFirestoreDoc(currDoc).toObject()
        usersInDb.push(horse)
      })

      setUserTeamData(uniqBy([...userTeamData.filter((user) => user.selected), ...usersInDb], 'id'))
    } catch (error) {
      console.error('error: ', error)
    } finally {
      setLoading(false)
    }
  }

  const changeHandle = (value: any) => {
    const updated = userTeamData.map((filter) => {
      if (filter.id === value.id) {
        return { ...filter, selected: !filter.selected }
      } else {
        return { ...filter }
      }
    })
    setUserTeamData(updated)
  }

  const saveHandle = async () => {
    setButtonLoading(true)
    const selected = userTeamData.filter((member) => member.selected)
    await dataToPassOn.onSaveStaff({ selected })
    setButtonLoading(false)
  }

  return (
    <MainModal
      type="ORGNAIZER_EVENT_STAFF_ADD"
      show={true}
      title={`Select members to add to ${title}`}
      size="md"
      buttons={[
        {
          disabled: loading || buttonLoading,
          loading: buttonLoading,
          label: 'Save',
          onClick: saveHandle,
        },
      ]}
      setHeightAsPerContent={true}
    >
      <div className="text-SeabiscuitDark200ThemeColor">
        <div className="inputSearch py-2 flex items-center sticky z-20 top-0 bg-white">
          <input
            type="text"
            placeholder="Search..."
            className="rounded-full w-full border focus:border-[#D3DAEE] border-[#D3DAEE] text-SeabiscuitDark200ThemeColor focus:ring-0"
            onChange={(e) => handleUsers(e.target.value)}
          />
          {dataToPassOn.showAddNewPersonFeature ? (
            <div className="absolute right-0 top-0 h-full w-[135px] flex justify-center items-center">
              <span className="absolute left-0 h-[60%] top-[50%] translate-y-[-50%] w-[1px] bg-[#122B4680]" />
              <button
                onClick={() => {
                  handleModalContext?.handleModal?.(true, MODAL_CONSTS.ADD_NEW_USER, {
                    ...handleModalContext?.dataToPassOn,
                  })
                }}
                className={
                  'group text-[#122B4680] text-[14px] flex items-center gap-1 hover:text-SeabiscuitMainThemeColor'
                }
              >
                <IconPlus /> New Person
              </button>
            </div>
          ) : (
            <Search className="absolute text-SeabiscuitMainThemeColor right-4" />
          )}
        </div>
        <div className="min-h-[300px] overflow-auto">
          {loading ? (
            <div className="w-full p-4 flex justify-center items-center">
              <ViewsLoader size="xl" color="red" />
            </div>
          ) : userTeamData?.[0] ? (
            <div className="py-4">
              {userTeamData?.map((member, index) => (
                <React.Fragment key={index}>
                  <SelectBox
                    member={member}
                    onClick={changeHandle}
                    selected={member?.selected ?? false}
                  />
                </React.Fragment>
              ))}
            </div>
          ) : (
            <p className="mt-2 text-center text-black/50">
              {searchValue.length < 3
                ? 'Please enter at least 3 letters in the search'
                : 'No result found.'}
            </p>
          )}
        </div>
      </div>
    </MainModal>
  )
}

export default StaffModal
